import { useState, useEffect, useCallback } from 'react';
import { C, GREEN } from '../constants';
import LoadErrorBanner from './LoadErrorBanner';

const API_URL = process.env.REACT_APP_MEDIA_STUDIO_API_URL || 'https://strategic-honesty-media-studio-production.up.railway.app';
const API_KEY = process.env.REACT_APP_API_KEY;

// Posts SHIOS pushes into the queue are held until a person approves them. They used to be reachable
// only on "Waiting to Publish" (under More). This puts them on Review & Publish itself, as a card in
// the same style as the pipeline cards, with the text and Approve / Reject right there. It shows
// nothing at all when nothing is waiting.

// Above this many characters the text box scrolls, and a line says so.
export const LONG_POST = 600;

export const NEXT_STEP =
  'Approved. It goes out in your next open LinkedIn slot (Wednesday or Saturday, 5 PM Central).';

export function postTitle(post) {
  const t = (post.title || '').trim();
  if (t) return t;
  const first = (post.content || '').split('\n').map((l) => l.trim()).find(Boolean);
  return first ? first.slice(0, 120) : 'LinkedIn post';
}

const box = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 14 };
const chip = { display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: C.blueLight, color: C.blue };

function when(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Presentational and pure, so what the person sees can be tested without a network.
export function SharedPostsApprovalView({
  posts = [], error = null, busyId = null, errors = {}, notice = null,
  onApprove = () => {}, onReject = () => {}, onDismiss = () => {}, onOpenWaiting = () => {},
}) {
  if (error) return <LoadErrorBanner status={error.status} />;
  if (!posts.length && !notice) return null;

  return (
    <section aria-label="LinkedIn posts waiting for your approval" style={box}>
      {notice && (
        <div role="status" style={{ background: '#f0fdf4', border: '1px solid #16a34a', borderRadius: 8, padding: '10px 12px', marginBottom: posts.length ? 12 : 0, fontSize: 13, color: '#14532d', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{notice}</span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onOpenWaiting} style={{ background: 'none', border: 'none', padding: 0, color: '#14532d', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>See Waiting to Publish</button>
            <button type="button" onClick={onDismiss} aria-label="Dismiss" style={{ background: 'none', border: 'none', padding: 0, color: '#14532d', cursor: 'pointer', fontSize: 15, lineHeight: 1 }}>{'×'}</button>
          </span>
        </div>
      )}

      {posts.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 15, color: C.text }}>{'🔎'} From SHIOS: LinkedIn {posts.length === 1 ? 'post' : 'posts'} for your approval</strong>
            <span style={chip}>{posts.length}</span>
          </div>
          <p style={{ margin: '4px 0 12px', fontSize: 12, color: C.muted }}>
            Read it, then approve to schedule it or reject to delete it. Nothing is posted until you approve.
          </p>

          {posts.map((post) => {
            const busy = busyId === post.id;
            return (
              <article key={post.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 10, background: C.blueLight }}>
                <div style={{ padding: '10px 12px', display: 'grid', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 14, color: C.text, minWidth: 0 }}>{postTitle(post)}</strong>
                    <span style={{ fontSize: 11, color: C.muted }}>{when(post.created_at)}</span>
                  </div>
                  <div
                    tabIndex={0}
                    role="region"
                    aria-label="Full text of the post"
                    style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', fontSize: 14, lineHeight: 1.65, color: C.text, whiteSpace: 'pre-wrap', overflowY: 'auto', maxHeight: 'min(60vh, 520px)' }}
                  >
                    {post.content}
                  </div>
                  {(post.content || '').length > LONG_POST && (
                    <div style={{ fontSize: 12, color: C.muted }}>Scroll inside the box to read the whole post.</div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <button type="button" disabled={busy} onClick={() => onApprove(post.id)} style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1, fontFamily: 'inherit' }}>
                      {busy ? 'Working…' : '✓ Approve and schedule'}
                    </button>
                    <button type="button" disabled={busy} onClick={() => onReject(post.id)} style={{ background: '#fff', color: C.red, border: '1px solid #fca5a5', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                      {'✗'} Reject
                    </button>
                  </div>
                  {errors[post.id] && <div role="alert" style={{ fontSize: 12, color: '#b91c1c' }}>{errors[post.id]}</div>}
                </div>
              </article>
            );
          })}
        </>
      )}
    </section>
  );
}

export default function SharedPostsApproval({ onOpenWaiting = () => {} }) {
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/buffer-queue/needs-approval`, { headers: { 'x-api-key': API_KEY } });
      if (!res.ok) { setError({ status: res.status }); setPosts([]); return; }
      const data = await res.json();
      setError(null);
      setPosts(data.success ? (data.posts || []) : []);
    } catch {
      setError({ status: 'network' });
      setPosts([]);
    }
  }, []);

  useEffect(() => {
    load();
    // Coming back to this tab after acting elsewhere should not show a stale list.
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  async function act(id, request, doneNotice) {
    setBusyId(id);
    setErrors((e) => ({ ...e, [id]: null }));
    try {
      const res = await request();
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setErrors((e) => ({ ...e, [id]: data.error || `That did not go through (HTTP ${res.status}). Try again.` }));
        return;
      }
      setNotice(doneNotice);
      await load();
    } catch {
      setErrors((e) => ({ ...e, [id]: 'Network error: that did not go through. Try again.' }));
    } finally {
      setBusyId(null);
    }
  }

  const approve = (id) => act(id, () => fetch(`${API_URL}/api/buffer-queue/${id}/approve`, { method: 'PATCH', headers: { 'x-api-key': API_KEY } }), NEXT_STEP);
  const reject = (id) => {
    if (!window.confirm('Reject and delete this LinkedIn draft? This cannot be undone.')) return;
    act(id, () => fetch(`${API_URL}/api/buffer-queue/${id}`, { method: 'DELETE', headers: { 'x-api-key': API_KEY } }), 'Rejected and deleted.');
  };

  return (
    <SharedPostsApprovalView
      posts={posts} error={error} busyId={busyId} errors={errors} notice={notice}
      onApprove={approve} onReject={reject} onDismiss={() => setNotice(null)} onOpenWaiting={onOpenWaiting}
    />
  );
}
